/**
 * StickyMobileCta — fixed bottom bar with Subscribe / Install PWA / Share CTAs.
 *
 * Visible only on mobile (≤768 px, detected via useViewportSize). Hidden on desktop.
 * Respects iOS safe-area-inset-bottom so the bar doesn't overlap the home indicator.
 *
 * Design rules:
 * - uncodixify-ui.md: rounded-md buttons, transition-colors only, no shadow escalation
 * - rtl-support-arabic.md: flex-row with no rtl:flex-row-reverse (dir="rtl" handles it)
 * - switch-component-styling.md: N/A — no switches in this component
 * - accessibility: every button ≥44×44px via p-3 min-h-[44px]; aria-labels present
 */

import type { TranslateFn } from "#/lib/i18n.js";
import { useViewportSize } from "#/hooks/useViewportSize.js";

export interface StickyMobileCtaProps {
  t: TranslateFn;
  /** Called when user taps Subscribe. Navigates to /subscribe. */
  onSubscribe?: () => void;
  /** Called when user taps Install. Triggers PWA install prompt. */
  onInstall?: () => void;
  /** Whether the PWA install prompt is available (from vite-plugin-pwa). */
  installAvailable?: boolean;
}

export function StickyMobileCta({
  t,
  onSubscribe,
  onInstall,
  installAvailable = false,
}: StickyMobileCtaProps) {
  const { isMobile } = useViewportSize();

  // Only render on mobile to avoid layout shift on desktop.
  if (!isMobile) return null;

  async function handleShare() {
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Hantavirus Outbreak Tracker",
          url: window.location.href,
        });
      } catch {
        // User cancelled or API not supported — silent.
      }
    }
  }

  return (
    <div
      className="sticky-mobile-cta"
      role="complementary"
      aria-label={t("dashboard:cta.subscribe")}
    >
      <div className="flex items-center gap-2">
        {/* Subscribe — primary action, full width on compact */}
        <button
          type="button"
          onClick={onSubscribe}
          className="flex flex-1 items-center justify-center gap-2 rounded-md bg-[var(--color-accent-emergency)] px-4 py-3 min-h-[44px] text-sm font-semibold text-white transition-colors duration-150 hover:bg-[var(--color-accent-emergency)]/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
          aria-label={t("dashboard:cta.subscribe")}
        >
          <svg
            aria-hidden
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            className="h-4 w-4 shrink-0"
          >
            <path d="M8 1v14M1 8h14" />
          </svg>
          <span>{t("dashboard:cta.subscribe")}</span>
        </button>

        {/* Install PWA — secondary, shown only when install prompt available */}
        {installAvailable && (
          <button
            type="button"
            onClick={onInstall}
            className="flex items-center justify-center rounded-md border border-[var(--color-border-default)] bg-[var(--color-bg-surface-elevated)] p-3 min-h-[44px] min-w-[44px] text-[var(--color-fg-muted)] transition-colors duration-150 hover:bg-[var(--color-bg-surface-elevated)]/80 hover:text-[var(--color-fg-default)]"
            aria-label={t("dashboard:cta.installPwa")}
          >
            <svg
              aria-hidden
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              className="h-4 w-4"
            >
              <path d="M8 1v9M4 7l4 3 4-3M1 12v2h14v-2" />
            </svg>
          </button>
        )}

        {/* Share — native share sheet */}
        {typeof navigator !== "undefined" && "share" in navigator && (
          <button
            type="button"
            onClick={handleShare}
            className="flex items-center justify-center rounded-md border border-[var(--color-border-default)] bg-[var(--color-bg-surface-elevated)] p-3 min-h-[44px] min-w-[44px] text-[var(--color-fg-muted)] transition-colors duration-150 hover:bg-[var(--color-bg-surface-elevated)]/80 hover:text-[var(--color-fg-default)]"
            aria-label={t("dashboard:cta.share")}
          >
            <svg
              aria-hidden
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              className="h-4 w-4"
            >
              <path d="M10 2l4 4-4 4M14 6H5a3 3 0 000 6h2" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
}
